/* eslint-disable arrow-parens */
import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { PermissionGuard } from './core/services/permission/permission.guard';
import {
    // configuration
    ORGANIZATION_PREFERENCE_ACCESS,
    FAQ_ACCESS,
    APP_UPDATE_ACCESS,
    STUDENT_FORM_ACCESS,
    WEB_PREFERENCE_ACCESS,
    BANNERS_ACCESS,
    BANNER_PACKAGE,

    // user
    CLIENTS,
    STUDENT_ACCESS,
    TEACHER_ACCESS,
    PARENT_ACCESS,
    VIRTUAL_USER_MAPPING,
    SUBSCRIBER_ACCESS,
    PW_CLIENT_S2S,
    LCM_ACCESS,

    // application
    PROGRAM_ACCESS,
    MCQ_ACCESS,
    COMPANY_ACCESS,
    STATE_ACCESS,
    ADMISSION_YEARS_ACCESS,
    SUBJECT_EXPERTISE,

    // management
    BATCH_CATEGORY_ACCESS,
    STUDY_MATERIAL_ACCESS,
    BATCH_MENTORS_ACCESS,
    MARGDARSHAK,
    COHORT_ACCESS,
    DOUBT_CATEGORY_ACCESS,
    ORDER_ACCESS,
    ORDER_CANCELLATION_ACCESS,
    REPORT_ACCESS,
    SAARTHI_ACCESS,
    TESTIMONIAL_ACCESS,
    ORG_ANNOUNCEMENTS_ACCESS,
    TEST_PASS,
    THREE_D_MODEL_ACCESS,
    COHORT_EVENT_ACCESS,
    SLIDE_IMAGE_ACCESS,

    // batch-automation,
    BATCH_ACCESS,

    // video-player
    ORGANIZATION_PREFERENCE_VIDEO_ACCESS,
    ANIMATIONS,
    BADGES,

    // videos
    DRIVE_SECURE_BULK_ACCESS,
    PENPENCIL_VIDEO_ACCESS,
    LIVE_STREAM_ACCESS,
    REELS,
    WEBINAR_UNIGO,

    // studio
    STUDIO_ACCESS,
    STUDIO_LOCATIONS,

    // feed
    FEEDS_ACCESS,

    // books
    EXAM_ACCESS,
    BOOK_CATEGORY_ACCESS,
    AUTHOR_ACCESS,
    PUBLICATION_ACCESS,
    BOOK_TAGS_ACCESS,
    DELIVERY_PIN_CODE_ACCESS,
    BOOK_ACCESS,

    // promotions
    NEWS_AND_BLOGS_ACCESS,
    JOB_ALERT,
    REWARD,
    COUPON_ACCESS,
    BULK_COUPON_ACCESS,

    // offline-centre
    OFFLINE_CATEGORY,
    PATHSHALA_CENTER_ACCESS,
    RFID_MACHINE_ACCESS,
    EVAL_DASHBOARD_ACCESS,
    SCHOLARSHIP_ACCESS,
    SAT_CATAGORY,
    WEBINAR_ACCESS,

    // sst
    REPORT_TAGS_ACCESS,
    HELPCENTER_ACCESS,
    SOURCE_FLOWS_ACCESS,
    USER_GROUPS_ACCESS,
    NOTICE_ACCESS,

    // engagement
    COACH_MARK,
    BATTLEGROUND_ACCESS,
    COMMUNITY_ACCESS,
    LIBRARY_REVAMP_ACCESS,
    LEVEL_UP_ACCESS,
    AI_NCERT_ACCESS,

    // loan-buddies
    LOAN_BUDDY,
    BANK,
    BHARAT_VIDEO_ACCESS,
    OTT_VIDEO,
    OTT_PLAYLIST,
    OTT_TEACHER,
    OTT_SUBSCRIPTION,
    ONBOARDING_LAYER_ACCESS,
    JOBS_ACCESS,
    OTT_BROWSE,
} from 'app/core/constants';

const defaultLoginRoutes = [
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-in',
                loadChildren: (): Promise<any> =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: (): Promise<any> =>
                    import('app/modules/auth/sign-out/sign-out.module').then(
                        (m) => m.AuthSignOutModule,
                    ),
            },
        ],
    },
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'faq-search',
                loadChildren: (): Promise<any> =>
                    import('app/modules/faq-search/faq-search.module').then(
                        (m) => m.FaqSearchModule,
                    ),
            },
        ],
    },
];

const configurationRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: 'all',
        },
        children: [
            {
                path: 'profile',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/profile/profile.module').then(
                        (m) => m.ProfileModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ORGANIZATION_PREFERENCE_ACCESS.list,
        },
        children: [
            {
                path: 'preferences',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/preferences/preferences.module'
                    ).then((m) => m.PreferencesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: FAQ_ACCESS.list,
        },
        children: [
            {
                path: 'faq-category',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/faq-category/faq-category.module'
                    ).then((m) => m.FaqCategoryModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: APP_UPDATE_ACCESS.list,
        },
        children: [
            {
                path: 'app-update',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/app-update/app-update.module'
                    ).then((m) => m.AppUpdateModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ORGANIZATION_PREFERENCE_ACCESS.business_list,
        },
        children: [
            {
                path: 'businessmodule',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/business/business.module').then(
                        (m) => m.BusinessModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_FORM_ACCESS.list,
        },
        children: [
            {
                path: 'student-form',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/student-form/student-form.module'
                    ).then((m) => m.StudentFormModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: WEB_PREFERENCE_ACCESS.list,
        },
        children: [
            {
                path: 'webpage',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/website-home/website-home.module'
                    ).then((m) => m.WebsiteHomeModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: WEB_PREFERENCE_ACCESS.list,
        },
        children: [
            {
                path: 'web-preference',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/web-preference/web-preference.module'
                    ).then((m) => m.WebPreferenceModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BANNERS_ACCESS.list,
        },
        children: [
            {
                path: 'banners',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/banners/banners.module').then(
                        (m) => m.BannersModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BANNER_PACKAGE.list,
        },
        children: [
            {
                path: 'banner-package',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/banner-packages/banner-packages.module'
                    ).then((m) => m.BannerPackagesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: WEB_PREFERENCE_ACCESS.list,
        },
        children: [
            {
                path: 'thankyou',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/thank-you/thank-you.module').then(
                        (m) => m.ThankYouModule,
                    ),
            },
        ],
    },
];

const userRoutes = [
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: CLIENTS.list,
        },
        children: [
            {
                path: 'clients',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/clients/clients.module'
                    ).then((m) => m.ClientsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_ACCESS.v2usersList,
        },
        children: [
            {
                path: 'users',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/users/users.module').then(
                        (m) => m.UsersModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: TEACHER_ACCESS.list,
        },
        children: [
            {
                path: 'admins',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/users/admin/admin.module').then(
                        (m) => m.AdminModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: 'all',
        },
        children: [
            {
                path: 'roles',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/user-roles/user-roles.module'
                    ).then((m) => m.UserRolesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: TEACHER_ACCESS.list,
        },
        children: [
            {
                path: 'teachers',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/teachers/teachers.module'
                    ).then((m) => m.TeachersModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_ACCESS.v2usersList,
        },
        children: [
            {
                path: 'global-user-cms',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/global-user-cms/global-user-cms.module'
                    ).then((m) => m.GlobalUserCmsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_ACCESS.v2usersList,
        },
        children: [
            {
                path: 'gyaane-user-cms',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/gyaane-user-cms/gyaane-user-cms.module'
                    ).then((m) => m.GyaaneUserCmsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_ACCESS.v2usersList,
        },
        children: [
            {
                path: 'offline-centre-client',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/offline-centre-client/offline-centre-client.module'
                    ).then((m) => m.OfflineCentreClientModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_ACCESS.list,
        },
        children: [
            {
                path: 'students',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/students/students.module'
                    ).then((m) => m.StudentsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: PARENT_ACCESS.list,
        },
        children: [
            {
                path: 'parents',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/parents/parents.module'
                    ).then((m) => m.ParentsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_ACCESS.v2usersList,
        },
        children: [
            {
                path: 'qbg_users',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/qbg-users/qbg-users.module'
                    ).then((m) => m.QbgUsersModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDENT_ACCESS.v2usersList,
        },
        children: [
            {
                path: 'sst-agents',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/sst-agents/sst-agents.module'
                    ).then((m) => m.SstAgentsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: TEACHER_ACCESS.list,
        },
        children: [
            {
                path: 'doubt-sme',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/doubt-sme/doubt-sme.module'
                    ).then((m) => m.DoubtSmeModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: VIRTUAL_USER_MAPPING.list,
        },
        children: [
            {
                path: 'virtual-user-mapping',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/virtual-user-mapping/virtual-user-mapping.module'
                    ).then((m) => m.VirtualUserMappingModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: SUBSCRIBER_ACCESS.list,
        },
        children: [
            {
                path: 'subscribers',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/users/subscribers/subscribers.module'
                    ).then((m) => m.SubscribersModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: PW_CLIENT_S2S.list,
        },
        children: [
            {
                path: 'pw-clients-s2s',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/pw-clients-s2s/pw-clients-s2s.module'
                    ).then((m) => m.PwClientsS2sModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: LCM_ACCESS.blockHistory,
        },
        children: [
            {
                path: 'odyssey',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/batch/odyssey/odyssey.module'
                    ).then((m) => m.OdysseyModule),
            },
        ],
    },
];

const applicationRoutes = [
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: 'all', // To treat as home page.
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/dashboard/dashboard.module').then(
                        (m) => m.DashboardModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: PROGRAM_ACCESS.list,
        },
        children: [
            {
                path: 'programs',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/programs/programs.module').then(
                        (m) => m.ProgramsModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: MCQ_ACCESS.list,
        },
        children: [
            {
                path: 'daily-updates',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/daily-updates/daily-updates.module'
                    ).then((m) => m.DailyUpdatesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: COMPANY_ACCESS.list,
        },
        children: [
            {
                path: 'company-details',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/company-details/company-details.module'
                    ).then((m) => m.CompanyDetailsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STATE_ACCESS.list,
        },
        children: [
            {
                path: 'state',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/state/state.module').then(
                        (m) => m.StateListModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ADMISSION_YEARS_ACCESS.list,
        },
        children: [
            {
                path: 'admission-years',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/admission-years/admission-years.module'
                    ).then((m) => m.AdmissionYearsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: SUBJECT_EXPERTISE.list,
        },
        children: [
            {
                path: 'subject-expertise',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/subject-expertise/subject-expertise.module'
                    ).then((m) => m.SubjectExpertiseModule),
            },
        ],
    },
];

const managementRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BATCH_CATEGORY_ACCESS.list,
        },
        children: [
            {
                path: 'batch-category',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/batch/batch-category/batch-category.module'
                    ).then((m) => m.BatchCategoryModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDY_MATERIAL_ACCESS.list,
        },
        children: [
            {
                path: 'study-material',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/study-material/study-material.module'
                    ).then((m) => m.StudyMaterialModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BHARAT_VIDEO_ACCESS.list,
        },
        children: [
            {
                path: 'bharat-video',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/batch/bharat-video/bharat-video.module'
                    ).then((m) => m.BharatVideoModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BATCH_MENTORS_ACCESS.list,
        },
        children: [
            {
                path: 'batch-mentors',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/batch-mentors/batch-mentors.module'
                    ).then((m) => m.BatchMentorsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: MARGDARSHAK.teacherList,
        },
        children: [
            {
                path: 'margdarshak',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/margdarshak/margdarshak.module'
                    ).then((m) => m.MargdarshakModule),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: DOUBT_CATEGORY_ACCESS.list,
        },
        children: [
            {
                path: 'doubts-category',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/doubts-category/doubts-category.module'
                    ).then((m) => m.DoubtsCategoryModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: 'all',
        },
        children: [
            {
                path: 'doubts',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/doubts/doubts.module').then(
                        (m) => m.DoubtsModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ORDER_ACCESS.list,
        },
        children: [
            {
                path: 'orders',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/orders/orders.module').then(
                        (m) => m.OrdersModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ORDER_CANCELLATION_ACCESS.list,
        },
        children: [
            {
                path: 'order-cancellation-requests',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/order-cancellation-requests/order-cancellation-requests.module'
                    ).then((m) => m.OrderCancellationRequestsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: REPORT_ACCESS.list,
        },
        children: [
            {
                path: 'reports',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/reports/reports.module').then(
                        (m) => m.ReportsModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: SAARTHI_ACCESS.list,
        },
        children: [
            {
                path: 'saarthi',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/saarthi/saarthi.module').then(
                        (m) => m.SaarthiModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: TESTIMONIAL_ACCESS.list,
        },
        children: [
            {
                path: 'testimonial',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/testimonial/testimonial.module'
                    ).then((m) => m.TestimonialModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ORG_ANNOUNCEMENTS_ACCESS.list,
        },
        children: [
            {
                path: 'org-announcements',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/org-announcements/org-announcements.module'
                    ).then((m) => m.OrgAnnouncementsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: TEST_PASS.list,
        },
        children: [
            {
                path: 'test-pass',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/test-pass/test-pass/test-pass.module'
                    ).then((m) => m.TestPassModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: THREE_D_MODEL_ACCESS.list,
        },
        children: [
            {
                path: '3d-models',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/three-d-model/three-d-model.module'
                    ).then((m) => m.ThreeDModelModule),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: SLIDE_IMAGE_ACCESS.list,
        },
        children: [
            {
                path: 'slide-image',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/slide-image/slide-image.module'
                    ).then((m) => m.SlideImageModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'deeplink-gen',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/deeplink-gen/deeplink-gen.module'
                    ).then((m) => m.DeepLinkGenModule),
            },
        ],
    },
];

const cohortRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: COHORT_ACCESS.list,
        },
        children: [
            {
                path: 'cohort',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/cohort/cohort.module').then(
                        (m) => m.CohortModule,
                    ),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: COHORT_EVENT_ACCESS.list,
        },
        children: [
            {
                path: 'cohort-events',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/cohort-events/cohort-events.module'
                    ).then((m) => m.CohortEventsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ONBOARDING_LAYER_ACCESS.rootListing,
        },
        children: [
            {
                path: 'onboarding-layer',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/onboarding-layer/onboarding-layer.module'
                    ).then((m) => m.OnboardingLayerModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: JOBS_ACCESS.getJobDetails,
        },
        children: [
            {
                path: 'job',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/jobs/job-list.module').then(
                        (m) => m.JobListModule,
                    ),
            },
            {
                path: 'jobs-config',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/jobs-config/jobs-config.module'
                    ).then((m) => m.JobsConfigModule),
            },
        ],
    },
];

const batchAutomationRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BATCH_ACCESS.addAnnouncement,
        },
        children: [
            {
                path: 'batch-automation-announcement',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/batch/batch-automation/batch-announcement/batch-announcement.module'
                    ).then((m) => m.BatchAutomationAnnouncementModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BATCH_ACCESS.addHomework,
        },
        children: [
            {
                path: 'batch-automation-homework',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/batch/batch-automation/batch-homework/batch-homework.module'
                    ).then((m) => m.BatchAutomationHomeworkModule),
            },
        ],
    },
];

const videoPlayerRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ORGANIZATION_PREFERENCE_VIDEO_ACCESS.list,
        },
        children: [
            {
                path: 'video-player-quality',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/video-player-quality/video-player-quality.module'
                    ).then((m) => m.VideoQualityModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ANIMATIONS.list,
        },
        children: [
            {
                path: 'animations',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/animations/animations.module'
                    ).then((m) => m.AnimationsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BADGES.list,
        },
        children: [
            {
                path: 'badges',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/badges/badges.module').then(
                        (m) => m.BadgesModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BADGES.list,
        },
        children: [
            {
                path: 'automated-badges',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/automated-badges/automated-badges.module'
                    ).then((m) => m.AutmoatedBadgesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ANIMATIONS.list,
        },
        children: [
            {
                path: 'automated-animations',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/automated-animations/automated-animations.module'
                    ).then((m) => m.AutomatedAnimationsModule),
            },
        ],
    },
];

const videosRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: DRIVE_SECURE_BULK_ACCESS.list,
        },
        children: [
            {
                path: 'drive-secure-bulk',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/drive-secure-bulk/drive-secure-bulk.module'
                    ).then((m) => m.DriveSecureBulkModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: PENPENCIL_VIDEO_ACCESS.list,
        },
        children: [
            {
                path: 'videos-tags',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/videos-tags/videos-tags.module'
                    ).then((m) => m.VideosTagsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: PENPENCIL_VIDEO_ACCESS.list,
        },
        children: [
            {
                path: 'videos-tags/videos/:tagId',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/videos-tags/penpencil-videos/penpencil-videos.module'
                    ).then((m) => m.PenpencilVideosModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: LIVE_STREAM_ACCESS.fetch,
        },
        children: [
            {
                path: 'secure-live',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/videos-tags/secure-live/secure-live.module'
                    ).then((m) => m.SecureLiveModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: LIVE_STREAM_ACCESS.harvest_fetch,
        },
        children: [
            {
                path: 'secure-live-videos',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/videos-tags/secure-live-videos/secure-live-videos.module'
                    ).then((m) => m.SecureLiveVideosModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: REELS.creatorsList,
        },
        children: [
            {
                path: 'reels',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/reels/reels.module').then(
                        (m) => m.ReelsModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: WEBINAR_UNIGO.list,
        },
        children: [
            {
                path: 'webinar-unigo',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/videos-tags/webinar-unigo/webinar-unigo.module'
                    ).then((m) => m.WebinarUnigoModule),
            },
        ],
    },
];

const studioRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDIO_ACCESS.list,
        },
        children: [
            {
                path: 'studio-list',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/studio/studio-list/studio-list.module'
                    ).then((m) => m.StudioListModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDIO_LOCATIONS.list,
        },
        children: [
            {
                path: 'studio-locations',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/studio/location/location.module'
                    ).then((m) => m.StudioLocationModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDIO_ACCESS.appVersionList,
        },
        children: [
            {
                path: 'studio-app-update',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/studio/studio-app-update/studio-app-update.module'
                    ).then((m) => m.StudioAppUpdateModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: LIVE_STREAM_ACCESS.fetch,
        },
        children: [
            {
                path: 'studio-live',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/studio/secure-live/secure-live.module'
                    ).then((m) => m.SecureLiveModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDIO_ACCESS.yt,
        },
        children: [
            {
                path: 'studio-yt',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/studio/studio-yt/studioyt.module'
                    ).then((m) => m.StudioYTModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDIO_ACCESS.allLectures,
        },
        children: [
            {
                path: 'teacherx-lectures',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/studio/teacherx-lectures/teacherx-lectures.module'
                    ).then((m) => m.TeacherXLecturesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: STUDIO_ACCESS.allLectures,
        },
        children: [
            {
                path: 'classroomx-lectures',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/studio/classroomx-lectures/classroomx-lectures.module'
                    ).then((m) => m.ClassroomXLecturesModule),
            },
        ],
    },
];

const ottRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: OTT_SUBSCRIPTION.planList,
        },
        children: [
            {
                path: 'ott-subscriptions',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/ott-subscription/ott-subscription.module'
                    ).then((m) => m.OttSubscriptionModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: OTT_VIDEO.list,
        },
        children: [
            {
                path: 'ott-cms-video',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/ott-cms/ott-cms-video/ott-cms-video.module'
                    ).then((m) => m.OttCMSVideoModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: OTT_PLAYLIST.list,
        },
        children: [
            {
                path: 'ott-cms-playlist',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/ott-cms/ott-cms-playlist/ott-cms-playlist.module'
                    ).then((m) => m.OttCMSPlaylistModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: OTT_TEACHER.list,
        },
        children: [
            {
                path: 'ott-cms-teacher',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/ott-cms/ott-cms-teacher/ott-cms-teacher.module'
                    ).then((m) => m.OttCMSTeacherModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'ott-cms-job-update',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/ott-cms/ott-cms-job-update/ott-cms-job-update.module'
                    ).then((m) => m.OttCMSJobUpdateModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: OTT_BROWSE.list,
        },
        children: [
            {
                path: 'ott-browse',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/ott-browse/ott-browse.module'
                    ).then((m) => m.OttBrowseModule),
            },
        ],
    },
];

const feedRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: FEEDS_ACCESS.list,
        },
        children: [
            {
                path: 'feed-tags',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/feed-tags/feed-tags.module').then(
                        (m) => m.FeedTagsModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: FEEDS_ACCESS.list,
        },
        children: [
            {
                path: 'feeds',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/feeds/feeds.module').then(
                        (m) => m.FeedsModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: FEEDS_ACCESS.list,
        },
        children: [
            {
                path: 'feed-tags;tag=:tag;tagName=:tagName',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/feeds/feeds.module').then(
                        (m) => m.FeedsModule,
                    ),
            },
        ],
    },
];

const booksRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: EXAM_ACCESS.list,
        },
        children: [
            {
                path: 'exam',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/books/exam/exam.module').then(
                        (m) => m.ExamModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BOOK_CATEGORY_ACCESS.list,
        },
        children: [
            {
                path: 'category',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/books/category/category.module'
                    ).then((m) => m.CategoryModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: AUTHOR_ACCESS.list,
        },
        children: [
            {
                path: 'author',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/books/author/author.module').then(
                        (m) => m.AuthorModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: PUBLICATION_ACCESS.list,
        },
        children: [
            {
                path: 'publication',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/books/publication/publication.module'
                    ).then((m) => m.PublicationModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BOOK_TAGS_ACCESS.list,
        },
        children: [
            {
                path: 'book-tags',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/books/book-tags/book-tags.module'
                    ).then((m) => m.BookTagsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: DELIVERY_PIN_CODE_ACCESS.list,
        },
        children: [
            {
                path: 'delivery-pincodes',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/books/delivery-pincode/delivery-pincode.module'
                    ).then((m) => m.DeliveryPincodeModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BOOK_ACCESS.list,
        },
        children: [
            {
                path: 'books',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/books/books.module').then(
                        (m) => m.BooksModule,
                    ),
            },
        ],
    },
];

const promotions = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: NEWS_AND_BLOGS_ACCESS.list,
        },
        children: [
            {
                path: 'news-and-blogs',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/news-and-blogs/news-and-blogs.module'
                    ).then((m) => m.NewsAndBlogsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: JOB_ALERT.list,
        },
        children: [
            {
                path: 'job-alerts',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/job-alerts/job-alerts.module'
                    ).then((m) => m.JobAlertsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: ORGANIZATION_PREFERENCE_ACCESS.refer_list,
        },
        children: [
            {
                path: 'referearn',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/referearn/referearn.module').then(
                        (m) => m.ReferearnModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: REWARD.list,
        },
        children: [
            {
                path: 'reward',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/reward/reward.module').then(
                        (m) => m.RewardModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: COUPON_ACCESS.list,
        },
        children: [
            {
                path: 'coupons',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/coupons/coupons.module').then(
                        (m) => m.CouponsModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BULK_COUPON_ACCESS.list,
        },
        children: [
            {
                path: 'bulk-coupons',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/coupons/bulk-coupons/bulk-coupons.module'
                    ).then((m) => m.BulkCouponsModule),
            },
        ],
    },
];

const offlineCentreRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: OFFLINE_CATEGORY.listOfflineVerticals,
        },
        children: [
            {
                path: 'offline-center',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/scholarship/offline-center/offline-centres.module'
                    ).then((m) => m.OfflineCategoryModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: PATHSHALA_CENTER_ACCESS.list,
        },
        children: [
            {
                path: 'offline-centres',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/pathshala/pathshala.module').then(
                        (m) => m.PathshalaModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: RFID_MACHINE_ACCESS.list,
        },
        children: [
            {
                path: 'centre-machine',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/offline-centre-machines/offline-centre-machines.module'
                    ).then((m) => m.OfflineMachinessModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: WEB_PREFERENCE_ACCESS.list,
        },
        children: [
            {
                path: 'centre-home',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/pathshala/offline-centre-home/offline-centre-home.module'
                    ).then((m) => m.OfflineCitiesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: EVAL_DASHBOARD_ACCESS.list,
        },
        children: [
            {
                path: 'eval-dashboard',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/eval-dashboard/eval-dashboard.module'
                    ).then((m) => m.EvalDashboardModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: SCHOLARSHIP_ACCESS.list,
        },
        children: [
            {
                path: 'scholarship-test',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/scholarship/scholarship-test/scholarship-test.module'
                    ).then((m) => m.ScholarshipTestModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: SAT_CATAGORY.list,
        },
        children: [
            {
                path: 'sat-management',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/scholarship/sat-catagory.module'
                    ).then((m) => m.SatCatagoryModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: WEBINAR_ACCESS.list,
        },
        children: [
            {
                path: 'webinar-video-category',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/webinar-category/webinar-category.module'
                    ).then((m) => m.WebinarCategoryModule),
            },
        ],
    },
];

const sstRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: REPORT_TAGS_ACCESS.list,
        },
        children: [
            {
                path: 'report-tag',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/report-tags/report-tags.module'
                    ).then((m) => m.ReportModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: HELPCENTER_ACCESS.get_preferences,
        },
        children: [
            {
                path: 'helpcenter-preferences',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/helpcenter/preferences/preferences.module'
                    ).then((m) => m.HelpCenterPreferencesModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: HELPCENTER_ACCESS.get_chat_flow_tree,
        },
        children: [
            {
                path: 'helpcenter-chat-flow',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/helpcenter/chat-flow/chat-flow.module'
                    ).then((m) => m.HelpCenterChatFlowModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: SOURCE_FLOWS_ACCESS.list,
        },
        children: [
            {
                path: 'source-flows',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/helpcenter/source-flows/source-flows.module'
                    ).then((m) => m.SourceFlowsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: USER_GROUPS_ACCESS.list,
        },
        children: [
            {
                path: 'user-groups',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/user-groups/user-groups.module'
                    ).then((m) => m.UserGroupsModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: NOTICE_ACCESS.list,
        },
        children: [
            {
                path: 'notices',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/notices/notices.module').then(
                        (m) => m.NoticesModule,
                    ),
            },
        ],
    },
];

const engagementRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: COACH_MARK.coachMark,
        },
        children: [
            {
                path: 'coach-marks',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/coach-mark/coach-mark-section.module'
                    ).then((m) => m.CoachMarkModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BATTLEGROUND_ACCESS.list,
        },
        children: [
            {
                path: 'battleground',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/battleground/battleground.module'
                    ).then((m) => m.BattlegroundModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: COMMUNITY_ACCESS.list,
        },
        children: [
            {
                path: 'community',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/community/community.module').then(
                        (m) => m.CommunityModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: LIBRARY_REVAMP_ACCESS.list,
        },
        children: [
            {
                path: 'library',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/library/library.module').then(
                        (m) => m.LibraryModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: LEVEL_UP_ACCESS.getSegments,
        },
        children: [
            {
                path: 'level-up',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/level-up/level-up.module').then(
                        (m) => m.LevelUpModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: AI_NCERT_ACCESS.bookList,
        },
        children: [
            {
                path: 'ai-ncert',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/ai-ncert/ai-ncert.module').then(
                        (m) => m.AiNcertModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {},
        children: [
            {
                path: 'sahayak',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/sahayak/sahayak.module').then(
                        (m) => m.SahayakModule,
                    ),
            },
        ],
    },
];

const loanBuddiesRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: LOAN_BUDDY.applicantList,
        },
        children: [
            {
                path: 'loan-buddies',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/loan-buddy/loan-buddy.module'
                    ).then((m) => m.LoanBuddyModule),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            permission: BANK.list,
        },
        children: [
            {
                path: 'banks-offers',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/admin/loan-buddy/banks-offer/banks.modules'
                    ).then((m) => m.BanksOffersModule),
            },
        ],
    },
];

const lessonRoutes = [
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'lesson/list',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/lesson/lesson.module').then(
                        (m) => m.LessonModule,
                    ),
            },
        ],
    },
];

const metaRoutes = [
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-exam',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-class',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-board',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-stream',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-language',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-board',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-exam-year',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-sub-exam',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
    {
        path: '',
        canActivate: [AuthGuard, PermissionGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'meta-finance-exam-category',
                loadChildren: (): Promise<any> =>
                    import('app/modules/admin/meta/config.module').then(
                        (m) => m.MetaConfigModule,
                    ),
            },
        ],
    },
];
export const appRoutes: Route[] = [
    ...defaultLoginRoutes,
    ...configurationRoutes,
    ...metaRoutes,
    ...userRoutes,
    ...applicationRoutes,
    ...managementRoutes,
    ...cohortRoutes,
    ...batchAutomationRoutes,
    ...videoPlayerRoutes,
    ...videosRoutes,
    ...studioRoutes,
    ...feedRoutes,
    ...booksRoutes,
    ...promotions,
    ...offlineCentreRoutes,
    ...sstRoutes,
    ...engagementRoutes,
    ...loanBuddiesRoutes,
    ...ottRoutes,
    ...lessonRoutes,
];
